// GeoMapWidget — country-level choropleth using react-simple-maps.
// iter-119ay: rendered for `chart_type === "geo_map"` widgets.
//
// Expected `data` shape: [{ label: "United States", value: 12345 }, ...]
// Labels can be country names ("United States") OR ISO2 codes ("US") OR
// ISO3 ("USA") — we normalise on render via a small alias table.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ComposableMap, Geographies, Geography, ZoomableGroup, Marker,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { scaleLinear } from "d3-scale";
import { CHART_COLORS, fmtTooltip } from "./chartFormat";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";

const StaleBadge = ({ reason }) => (
    <Badge
        variant="outline"
        className="border-amber-500/60 text-amber-600 font-mono text-[10px] gap-1"
        title={reason || "Source column no longer in dataset"}
    >
        <AlertTriangle className="h-3 w-3" /> STALE
    </Badge>
);

// Public CDN-hosted TopoJSON (110m world atlas, ~100kb). Loaded lazily by the
// library so the bundle stays small.
const GEO_URL =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const ADMIN1_GEO_URL =
    "https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@master/geojson/ne_50m_admin_1_states_provinces.geojson";

function getComputedColor(cssVar, fallback) {
    if (typeof window === "undefined" || !window.document) return fallback;
    let styleVal = window.getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
    if (!styleVal) {
        styleVal = window.getComputedStyle(document.body).getPropertyValue(cssVar).trim();
    }
    if (!styleVal) return fallback;
    if (styleVal.includes("var(")) {
        const match = styleVal.match(/var\((--[^,)]+)/);
        if (match && match[1]) {
            return getComputedColor(match[1], fallback);
        }
    }
    return styleVal;
}

function formatHslToCommaSeparated(hslStr) {
    if (!hslStr) return hslStr;
    let clean = hslStr.trim();
    if (/^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/.test(clean)) {
        clean = `hsl(${clean})`;
    }
    const hslMatch = clean.match(/^hsla?\(([^)]+)\)$/i);
    if (hslMatch) {
        const parts = hslMatch[1].trim().split(/[\s,]+/);
        if (parts.length >= 3) {
            const h = parts[0];
            const s = parts[1];
            const l = parts[2];
            return `hsl(${h}, ${s}, ${l})`;
        }
    }
    return clean;
}

function resolveCssColor(colorStr, fallback = "#cccccc") {
    if (!colorStr) return fallback;
    const trimmed = colorStr.trim();
    let resolved = trimmed;
    if (trimmed.startsWith("var(")) {
        const varMatch = trimmed.match(/var\((--[^,)]+)(?:,\s*([^)]+))?\)/);
        if (varMatch) {
            const varName = varMatch[1];
            const fallbackVal = varMatch[2] ? varMatch[2].trim() : fallback;
            resolved = getComputedColor(varName, resolveCssColor(fallbackVal, fallback));
        }
    } else if (trimmed.startsWith("hsl(var(")) {
        const varMatch = trimmed.match(/hsl\(var\((--[^)]+)\)\)/);
        if (varMatch) {
            const varName = varMatch[1];
            resolved = getComputedColor(varName, fallback);
        }
    }
    return formatHslToCommaSeparated(resolved);
}

// ISO-2 / ISO-3 / common-name aliases → match `properties.name` from the topo.
// iter-121.56 — Expanded alias table: lowercase keys for case-insensitive
// lookup, common synonyms (UAE / USA / U.K. etc.). Was previously ~16
// entries and missed lowercase / e-commerce-style values.
const COUNTRY_ALIAS = {
    US: "United States of America", USA: "United States of America",
    "United States": "United States of America",
    "united states": "United States of America",
    "united states of america": "United States of America",
    UK: "United Kingdom", GB: "United Kingdom", GBR: "United Kingdom",
    "united kingdom": "United Kingdom",
    DE: "Germany", DEU: "Germany", germany: "Germany",
    FR: "France", FRA: "France", france: "France",
    IN: "India", IND: "India", india: "India",
    BR: "Brazil", BRA: "Brazil", brazil: "Brazil",
    CA: "Canada", CAN: "Canada", canada: "Canada",
    AU: "Australia", AUS: "Australia", australia: "Australia",
    JP: "Japan", JPN: "Japan", japan: "Japan",
    CN: "China", CHN: "China", china: "China",
    MX: "Mexico", MEX: "Mexico", mexico: "Mexico",
    IT: "Italy", ITA: "Italy", italy: "Italy",
    ES: "Spain", ESP: "Spain", spain: "Spain",
    NL: "Netherlands", NLD: "Netherlands", netherlands: "Netherlands",
    SE: "Sweden", SWE: "Sweden", sweden: "Sweden",
    SG: "Singapore", SGP: "Singapore", singapore: "Singapore",
    AE: "United Arab Emirates", UAE: "United Arab Emirates",
    "united arab emirates": "United Arab Emirates",
    ZA: "South Africa", ZAF: "South Africa", "south africa": "South Africa",
    RU: "Russia", RUS: "Russia", russia: "Russia",
    AR: "Argentina", ARG: "Argentina", argentina: "Argentina",
    NZ: "New Zealand", NZL: "New Zealand", "new zealand": "New Zealand",
    KR: "South Korea", KOR: "South Korea", "south korea": "South Korea",
    ID: "Indonesia", IDN: "Indonesia", indonesia: "Indonesia",
};

function normaliseCountry(label) {
    if (!label) return "";
    const s = String(label).trim();
    // iter-121.56 — case-insensitive lookup. Previous impl only matched
    // exact case or full-upper. So "united states" / "USA" worked, but
    // "United states" / "usa" silently fell through and never lit up
    // the choropleth.
    return COUNTRY_ALIAS[s]
        || COUNTRY_ALIAS[s.toUpperCase()]
        || COUNTRY_ALIAS[s.toLowerCase()]
        || s;
}

function keyText(v) {
    return String(v ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function countryKeys(label) {
    const norm = normaliseCountry(label);
    return new Set([keyText(label), keyText(norm)].filter(Boolean));
}

function admin1NameKeys(props = {}) {
    const raw = [
        props.name,
        props.name_en,
        props.name_local,
        props.woe_name,
        props.gn_name,
        props.postal,
        props.abbrev,
        props.iso_3166_2,
        props.code_hasc,
    ];
    if (props.name_alt) raw.push(...String(props.name_alt).split("|"));
    return new Set(raw.map(keyText).filter(Boolean));
}

function admin1CountryKeys(props = {}) {
    const raw = [
        props.admin,
        props.geonunit,
        props.iso_a2,
        props.adm0_a3,
        props.gu_a3,
        props.sov_a3,
    ];
    return new Set(raw.flatMap((v) => [...countryKeys(v)]).filter(Boolean));
}

function matchAdmin1Row(row, props) {
    const label = keyText(row?.label);
    if (!label || !admin1NameKeys(props).has(label)) return false;
    if (!row?.country) return true;
    const rowCountries = countryKeys(row.country);
    const featureCountries = admin1CountryKeys(props);
    for (const k of rowCountries) {
        if (featureCountries.has(k)) return true;
    }
    return false;
}

function inferGeoLevel(w, cfg) {
    const explicit = String(w?.geo_level || cfg?.geo_level || "").toLowerCase();
    if (["state", "region", "admin1", "province"].includes(explicit)) return "admin1";
    if (explicit === "country") return "country";
    const raw = w?.group_by || cfg?.group_by || w?.x_field || cfg?.x || cfg?.field;
    const first = Array.isArray(raw) ? raw[0] : raw;
    const field = String(first || "").split(".").pop().toLowerCase();
    if (/(^|_)(state|province|region|admin1|admin_1)(_|$)/.test(field)) return "admin1";
    return "country";
}

export default function GeoMapWidget({ w, noCard = false }) {
    const cfg = w.config || {};
    // const data = cfg.data || [];
    const data = useMemo(() => cfg.data || [], [cfg.data]);
    const stale = cfg.stale;
    const geoLevel = inferGeoLevel(w, cfg);
    const isAdmin1 = geoLevel === "admin1";
    const admin1Url = cfg.geo_admin1_url || w.geo_admin1_url || ADMIN1_GEO_URL;
    const [adminGeography, setAdminGeography] = useState(null);
    useEffect(() => {
        if (!isAdmin1) return;
        let alive = true;
        fetch(admin1Url)
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => { if (alive) setAdminGeography(json); })
            .catch(() => { if (alive) setAdminGeography(null); });
        return () => { alive = false; };
    }, [isAdmin1, admin1Url]);

    const [position, setPosition] = useState({ coordinates: [0, 10], zoom: 1 });
    // iter-121.36 — Show value labels on each shaded region by default.
    // Users had to hover to learn any number. Per-widget escape hatch via
    // `widget.options.show_value_labels = false` for cases where regions
    // are too small (e.g. dense state maps).
    const showLabels = (w.options?.show_value_labels !== false);

    // Build a name → value lookup table once per render.
    const valueByCountry = useMemo(() => {
        const out = {};
        for (const row of data) {
            const k = normaliseCountry(row.label);
            if (k) out[k] = (out[k] || 0) + (Number(row.value) || 0);
        }
        return out;
    }, [data]);

    const admin1Matches = useMemo(() => {
        if (!isAdmin1 || !adminGeography?.features) {
            return { byFeature: {}, matchedLabels: new Set(), centers: [] };
        }
        const byFeature = {};
        const matchedLabels = new Set();
        const centers = [];
        for (const feature of adminGeography.features) {
            const props = feature.properties || {};
            const featureId = props.adm1_code || props.iso_3166_2 || props.code_hasc || props.name;
            let value = 0;
            let label = props.name_en || props.name;
            for (const row of data) {
                if (!matchAdmin1Row(row, props)) continue;
                value += Number(row.value) || 0;
                label = row.label || label;
                matchedLabels.add(keyText(row.label));
            }
            if (value) {
                byFeature[featureId] = { value, label };
                const lon = Number(props.longitude);
                const lat = Number(props.latitude);
                if (Number.isFinite(lon) && Number.isFinite(lat)) centers.push([lon, lat]);
            }
        }
        return { byFeature, matchedLabels, centers };
    }, [adminGeography, data, isAdmin1]);

    const valueByGeo = isAdmin1 ? admin1Matches.byFeature : valueByCountry;

    const maxValue = useMemo(() => {
        let m = 0;
        for (const v of Object.values(valueByGeo)) {
            const n = typeof v === "object" ? v.value : v;
            if (n > m) m = n;
        }
        return m || 1;
    }, [valueByGeo]);

    // Linear scale from 0 → max value across two brand-tinted shades.
    // Uses `CHART_COLORS[0]` (which respects the active theme accent) so the
    // map stays cohesive with the rest of the dashboard.
    const colorScale = useMemo(() => {
        const startColor = resolveCssColor("hsl(var(--muted))", "#f1f5f9");
        const endColor = resolveCssColor(CHART_COLORS[0], "#8b5cf6");
        return scaleLinear()
            .domain([0, maxValue])
            .range([startColor, endColor]);
    }, [maxValue]);

    const isEmpty = !data.length;
    // iter-121.56 — Detect "data exists but no country matches" so we
    // render an explanatory empty state instead of a silent blank world
    // map. This fires when the LLM grouped by region/continent
    // ("EMEA", "APAC", "Northeast") — none of which exist in the
    // topojson — but the chart still has rows.
    const noCountryMatch = !isEmpty && !isAdmin1 && Object.keys(valueByCountry).length === 0;
    const noAdmin1Match = !isEmpty && isAdmin1 && adminGeography?.features && Object.keys(valueByGeo).length === 0;
    // iter-121.56 — React-state tooltip (replaces unreliable SVG <title>
    // which only surfaces after a 1-3 second hover delay on most
    // browsers and never works on touch devices). Renders an absolute-
    // positioned chip near the cursor.
    const [hover, setHover] = useState(null); // { name, value, x, y }
    // iter-120 round-4 · responsive projection scale. The previous
    // hardcoded `scale: 130` looked OK at ~600px wide cards but the
    // map collapsed to ~25% of a 1900px-wide container (the white
    // gutters on either side of the world the user complained about).
    // Now we observe the container width and height and pick a scale that fits
    // the Equal-Earth projection edge-to-edge.
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    useEffect(() => {
        if (!containerRef.current || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) {
                const w = e.contentRect?.width;
                const h = e.contentRect?.height;
                if (w && h) {
                    setDimensions((prev) => {
                        if (Math.abs(prev.width - w) > 8 || Math.abs(prev.height - h) > 8) {
                            return { width: w, height: h };
                        }
                        return prev;
                    });
                }
            }
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);
    // Equal-Earth fits a world map into a 2:1 box, but the default SVG aspect
    // ratio is 4:3. We scale based on the actual rendered width of the map SVG.
    const aspect = 4 / 3;
    const actualW = Math.min(dimensions.width, dimensions.height * aspect);

    const adminCenter = useMemo(() => {
        const centers = admin1Matches.centers || [];
        if (!centers.length) return [0, 10];
        const lon = centers.reduce((a, c) => a + c[0], 0) / centers.length;
        const lat = centers.reduce((a, c) => a + c[1], 0) / centers.length;
        return [lon, lat];
    }, [admin1Matches.centers]);
    const projectionCenter = isAdmin1 ? adminCenter : [0, 10];
    const projectionScale = isAdmin1
        ? Math.max(260, Math.min(900, actualW / (admin1Matches.centers.length <= 1 ? 1.6 : 2.6)))
        : Math.max(130, Math.min(360, actualW / 5.6));

    useEffect(() => {
        setPosition({ coordinates: projectionCenter, zoom: 1 });
    }, [projectionCenter[0], projectionCenter[1]]);
    if (isEmpty || noCountryMatch || noAdmin1Match) {
        return (
            <div
                className={noCard ? "h-full flex flex-col" : "border border-border bg-card rounded-sm p-5 h-full flex flex-col"}
                data-testid={`widget-${w.id}`}
            >
                {!noCard && (
                    <div className="flex items-start justify-between mb-1">
                        <div className="eyebrow">{w.title}</div>
                        {stale && <StaleBadge reason={cfg.stale_reason} />}
                    </div>
                )}
                <div className="flex-1 mt-3 min-h-0 flex flex-col items-center justify-center text-center gap-2 px-4">
                    {isEmpty ? (
                        <>
                             <div className="text-xs text-muted-foreground max-w-sm">
                                 No {isAdmin1 ? "state / region" : "country"} data yet.
                             </div>
                             <div className="text-[10px] font-mono text-muted-foreground/70 uppercase tracking-wider">
                                 Map breakdown will appear when your source includes a mappable geography column.
                             </div>
                        </>
                    ) : (
                        <>
                             {/* iter-121.56 — Smarter empty state. Data exists but
                                 none of the labels match a country in the
                                 topojson (e.g. "EMEA", "APAC", "Northeast").
                                 Show the raw values so the widget isn't useless
                                 AND guide the user toward the right dimension. */}
                             <div className="text-xs text-muted-foreground max-w-sm">
                                 Dimension <span className="font-mono">"{data[0]?.label}"</span> doesn't map to a {isAdmin1 ? "state / region" : "country"}.
                             </div>
                             <div className="text-[10px] font-mono text-muted-foreground/70 uppercase tracking-wider mb-2">
                                 {isAdmin1
                                     ? "Add a country column when possible to disambiguate duplicate region names. Showing raw values below."
                                     : <>Edit the widget and group by <span className="text-primary">country</span> / <span className="text-primary">country_code</span> to render the map. Showing raw values below.</>}
                             </div>
                             <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono w-full max-w-md">
                                 {data.slice(0, 10).map((d) => (
                                     <div key={d.label} className="flex justify-between border-b border-border/40 py-0.5">
                                         <span className="truncate text-muted-foreground">{d.label}</span>
                                         <span className="text-foreground">{fmtTooltip(d.value, cfg.y_is_currency)}</span>
                                     </div>
                                 ))}
                             </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={noCard ? "h-full flex flex-col" : "border border-border bg-card rounded-sm p-5 h-full flex flex-col"}
            data-testid={`widget-${w.id}`}
        >
            {!noCard && (
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
            )}
            <div className="flex-1 mt-3 min-h-0 flex gap-4 overflow-hidden">
                <div className="relative w-full min-w-0" ref={containerRef}>
                    <ComposableMap
                        projection="geoEqualEarth"
                        projectionConfig={{ scale: projectionScale, center: projectionCenter }}
                        style={{ width: "100%", height: "100%" }}
                        width={800}
                        height={600}
                        data-testid={`geo-map-${w.id}`}
                    >
                        <ZoomableGroup
                            zoom={position.zoom}
                            center={position.coordinates}
                            onMoveEnd={setPosition}
                        >
                            {isAdmin1 && (
                                <Geographies geography={GEO_URL}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => (
                                            <Geography
                                                key={`base-${geo.rsmKey}`}
                                                geography={geo}
                                                fill="hsl(var(--muted)/0.4)"
                                                stroke="hsl(var(--border))"
                                                strokeWidth={0.4}
                                                style={{
                                                    default: { outline: "none" },
                                                    hover: { outline: "none" },
                                                    pressed: { outline: "none" },
                                                }}
                                            />
                                        ))
                                    }
                                </Geographies>
                            )}
                            <Geographies geography={isAdmin1 ? (adminGeography || admin1Url) : GEO_URL}>
                                {({ geographies }) =>
                                    <>
                                        {geographies.map((geo) => {
                                            const props = geo.properties || {};
                                            const featureId = props.adm1_code || props.iso_3166_2 || props.code_hasc || props.name;
                                            const match = isAdmin1 ? valueByGeo[featureId] : null;
                                            const name = isAdmin1
                                                ? (match?.label || props.name_en || props.name)
                                                : props.name;
                                            const hasData = isAdmin1 ? !!match : (name in valueByCountry);
                                            const v = isAdmin1 ? (match?.value ?? 0) : (valueByCountry[name] ?? 0);
                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    fill={hasData ? colorScale(v) : "hsl(var(--muted)/0.4)"}
                                                    stroke="hsl(var(--border))"
                                                    strokeWidth={0.4}
                                                    onMouseEnter={(e) => {
                                                        if (!hasData) {
                                                            setHover(null);
                                                            return;
                                                        }
                                                        const rect = containerRef.current?.getBoundingClientRect();
                                                        const x = rect ? e.clientX - rect.left : e.clientX;
                                                        const y = rect ? e.clientY - rect.top : e.clientY;
                                                        setHover({
                                                            name,
                                                            value: v,
                                                            hasData,
                                                            x,
                                                            y,
                                                        });
                                                    }}
                                                    onMouseMove={(e) => {
                                                        if (!hasData) {
                                                            setHover(null);
                                                            return;
                                                        }
                                                        const rect = containerRef.current?.getBoundingClientRect();
                                                        const x = rect ? e.clientX - rect.left : e.clientX;
                                                        const y = rect ? e.clientY - rect.top : e.clientY;
                                                        setHover((prev) => prev
                                                            ? { ...prev, x, y }
                                                            : null);
                                                    }}
                                                    onMouseLeave={() => setHover(null)}
                                                    style={{
                                                        default: { outline: "none" },
                                                        hover: {
                                                            outline: "none",
                                                            fill: CHART_COLORS[1] || CHART_COLORS[0],
                                                            cursor: "pointer",
                                                        },
                                                        pressed: { outline: "none" },
                                                    }}
                                                />
                                            );
                                        })}
                                        {/* iter-121.36 — Inline value labels at each region's centroid
                                        so users see numbers without hovering. Only shown for regions
                                        with a non-zero value to avoid noise. Per-widget opt-out via
                                        `widget.options.show_value_labels = false`. */}
                                        {showLabels && geographies.map((geo) => {
                                            const props = geo.properties || {};
                                            const featureId = props.adm1_code || props.iso_3166_2 || props.code_hasc || props.name;
                                            const match = isAdmin1 ? valueByGeo[featureId] : null;
                                            const name = isAdmin1
                                                ? (match?.label || props.name_en || props.name)
                                                : props.name;
                                            const v = isAdmin1 ? (match?.value || 0) : (valueByCountry[name] || 0);
                                            if (!v) return null;
                                            const [lng, lat] = geoCentroid(geo);
                                            return (
                                                <Marker key={`label-${geo.rsmKey}`} coordinates={[lng, lat]}>
                                                    <text
                                                        textAnchor="middle"
                                                        style={{
                                                            fontFamily: "var(--font-mono, ui-monospace)",
                                                            fontSize: "7px",
                                                            fontWeight: 600,
                                                            fill: "hsl(var(--foreground))",
                                                            stroke: "hsl(var(--background))",
                                                            strokeWidth: 1.5,
                                                            paintOrder: "stroke",
                                                            pointerEvents: "none",
                                                        }}
                                                    >
                                                        {fmtTooltip(v, cfg.y_is_currency)}
                                                    </text>
                                                </Marker>
                                            );
                                        })}
                                    </>
                                }
                            </Geographies>
                        </ZoomableGroup>
                    </ComposableMap>
                    {hover && (
                        <div
                            className="pointer-events-none absolute z-50 px-2 py-1 border border-border bg-card text-foreground rounded-sm shadow-md text-[11px] font-mono whitespace-nowrap"
                            style={{ left: hover.x + 12, top: hover.y + 12 }}
                            data-testid={`geo-map-tooltip-${w.id}`}
                        >
                            <span className="font-semibold">{hover.name}</span>
                            <span className="text-muted-foreground">
                                {": "}
                                {hover.hasData
                                    ? fmtTooltip(hover.value, cfg.y_is_currency)
                                    : "no data"}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Legend strip — min / mid / max swatches */}
            <div className="flex items-center justify-end gap-1 mt-2 text-[10px] font-mono text-muted-foreground/80 uppercase tracking-wider">
                <span>low</span>
                <div className="h-1.5 w-24 rounded-sm" style={{
                    background: `linear-gradient(to right, hsl(var(--muted)), ${CHART_COLORS[0]})`,
                }} />
                <span>high</span>
            </div>
        </div>
    );
}
